import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag } from '../../../redux/ControlPanel/TagControl/TagControl.type';
import SelectComponent, { SelectOptionProps } from '../../Input/Select.component';
import BaseModalComponent, { StyledBaseModal } from '../BaseModal.component';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ButtonComponent from '../../Button.component';
import { getTagListThunk } from '../../../redux/Tag/Tag.thunk';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';

interface TagEditModalProps extends React.AllHTMLAttributes<HTMLDivElement> {
  initialValues?: Tag[];
  onSave: (tags: Tag[]) => void;
  maxCount: number;
}

const StyledFooter = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px
`;

const StyledCandidateEditModal = styled.div`
  ${StyledBaseModal} {
    width: 500px;
  }
`;

const TagsEditModal = ({
    initialValues,
    onSave,
    maxCount,
  }: TagEditModalProps) => {
  const [tags, setTags] = useState<SelectOptionProps[]>([]);
  const { t } = useTranslation()
  const dispatch = useAppDispatch();
  const { closeModal } = useModalManager()

  useEffect(() => {
    const options = (initialValues || []).map(({ id, name: label }) => ({
      value: id,
      label,
    }));
    setTags(options);
  }, [initialValues]);

  const tagsLoadOptions = useCallback(
    async (search: string, loadedOptions: string | any[], { page }: any) => {
      const {
        // @ts-ignore
        payload: { data, pagination },
      } = await dispatch(getTagListThunk({search, page}));
      return {
        options: data.map((item: Tag) => ({
          value: item.id,
          label: t(item.name),
        })),
        hasMore: pagination.current_page < pagination.last_page,
        additional: {
          page: pagination.current_page + 1,
        },
      };
    },
    [],
  );

  const handleChangeTags = (tags: SelectOptionProps[]) => {
    setTags(tags);
  }

  const handleClose = () => {
    closeModal();
  }

  const handleSave = () => {
    const result = tags.map(({value: id, label: name}) => ({
      id,
      name
    } as Tag));
    onSave?.(result)
    closeModal();
  }

  const renderHeader = useMemo(() => {
    return t('Edit tags');
  }, []);

  const renderFooter = useMemo(() => (
    <StyledFooter>
      <ButtonComponent handleClick={handleSave}
        appearance="primary">
        {t('Add')}
      </ButtonComponent>
      <ButtonComponent handleClick={handleClose}>
        { t('Cancel') }
      </ButtonComponent>
    </StyledFooter>
  ), [tags])

  return (
    <StyledCandidateEditModal>
      <BaseModalComponent
        header={renderHeader}
        footer={renderFooter}>
        <SelectComponent
          useMenuPortalTarget
          label={t('Tags')}
          placeholder={t('Choose tags')}
          value={tags}
          isSearchable
          isOptionDisabled={maxCount === tags.length}
          isMulti
          loadOptions={tagsLoadOptions}
          onChangeOption={handleChangeTags}
        />
      </BaseModalComponent>
    </StyledCandidateEditModal>
  );
};

export default TagsEditModal;
